import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import './AIChatbot.css';

// ── Suggested quick prompts ──────────────────────────────────
const QUICK_PROMPTS = [
  { icon: '🎊', text: 'Best decorator dhundho mere liye' },
  { icon: '💰', text: 'Decoration ka price kya hoga?' },
  { icon: '📅', text: 'Booking kaise karte hain?' },
  { icon: '⭐', text: 'Verified decorators kaun hain?' },
  { icon: '📍', text: 'Raipur mein decorator chahiye' },
  { icon: '💍', text: 'Shaadi decoration budget 50k mein' },
];

// ── Typing indicator dots ───────────────────────────────────
const TypingDots = () => (
  <div className="typing-indicator">
    <span /><span /><span />
  </div>
);

// ── Single message bubble ───────────────────────────────────
const MessageBubble = ({ msg, isLatest }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`msg-row ${isUser ? 'user' : 'bot'} ${isLatest ? 'latest' : ''}`}>
      {!isUser && (
        <div className="bot-avatar">
          <span>✨</span>
        </div>
      )}
      <div className={`bubble ${isUser ? 'user-bubble' : 'bot-bubble'}`}>
        {msg.content.split('\n').map((line, i) => (
          <span key={i}>
            {line}
            {i < msg.content.split('\n').length - 1 && <br />}
          </span>
        ))}
        <div className="msg-time">
          {new Date(msg.timestamp).toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' })}
          {isUser && <span className="tick">✓✓</span>}
        </div>
      </div>
      {isUser && (
        <div className="user-avatar">
          <span>👤</span>
        </div>
      )}
    </div>
  );
};

export default function AIChatbot() {
  const { user } = useAuth();
  const [isOpen, setIsOpen]         = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [showQuick, setShowQuick]   = useState(true);
  const [hasNew, setHasNew]         = useState(false);
  const [error, setError]           = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const sessionRef     = useRef([]);  // Gemini conversation history

  // ── Initial greeting ────────────────────────────────────
  useEffect(() => {
    const greeting = {
      role: 'bot',
      content: `Namaste ${user?.name ? user.name.split(' ')[0] : 'ji'}! 🙏\n\nMain hun **DecorHub AI Assistant** — aapki decoration ki poori journey mein madad karunga!\n\n🎊 Decorator dhundne mein\n💰 Price estimate karne mein\n📅 Booking process mein\n⭐ Best options suggest karne mein\n\nKya poochna hai aapko?`,
      timestamp: Date.now(),
    };
    setMessages([greeting]);
  }, [user]);

  // ── Auto scroll ──────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // ── Focus input on open ──────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setHasNew(false);
    }
  }, [isOpen]);

  // ── Build system prompt ──────────────────────────────────
  const buildSystemPrompt = useCallback(() => {
    return `Tu DecorHub ka AI Assistant hai — Chhattisgarh ka #1 decoration booking platform.

TERA NAAM: DecorHub AI ✨
TERI BHASHA: Hinglish (Hindi + English mix) — casual, friendly, helpful
TERA KAAM: Users ko decoration booking mein help karna

PLATFORM KE BAARE MEIN:
- DecorHub pe verified decorators hain Chhattisgarh ke
- Services: Shaadi, Birthday, Corporate Events, Baby Shower, Anniversary
- Cities: Raipur, Durg, Bilaspur, Korba aur aaspaas
- Booking easy hai — search karo, profile dekho, book karo
- Payment secure hai — Razorpay se
- Reviews verified hain — sirf booked clients de sakte hain

PRICE RANGES (approximate):
- Simple Birthday: ₹3,000 - ₹10,000
- Birthday Premium: ₹10,000 - ₹30,000
- Shaadi Basic: ₹25,000 - ₹75,000
- Shaadi Premium: ₹75,000 - ₹3,00,000
- Corporate Event: ₹15,000 - ₹1,00,000
- Baby Shower: ₹5,000 - ₹20,000

RULES:
1. Hamesha helpful aur positive reh
2. Price puchhe toh range do, exact mat bolo kyunki vary karta hai
3. Koi specific decorator recommend karna ho toh "platform pe search karo" bolo
4. Booking ke liye hamesha platform pe jaane ko bolo
5. 2-3 short paragraphs mein reply karo — bahut lamba mat karo
6. Emojis use karo — friendly lagta hai
7. Agar kuch nahi pata toh honestly bolo aur admin se contact karne bolo
8. User ka naam pata ho toh use karo

USER INFO: ${user ? `Name: ${user.name}, Role: ${user.role}, City: ${user.city || 'Unknown'}` : 'Guest user'}
CURRENT TIME: ${new Date().toLocaleString('hi-IN', { timeZone: 'Asia/Kolkata' })}`;
  }, [user]);

  // ── Call Gemini API ──────────────────────────────────────
const callGemini = useCallback(async (userMessage) => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  if (!apiKey) throw new Error('API key missing');

  const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: 'meta-llama/llama-4-scout-17b-16e-instruct',
      messages: [
        { role: 'system', content: buildSystemPrompt() },
        ...sessionRef.current.map(m => ({
          role: m.role === 'bot' ? 'assistant' : 'user',
          content: m.content
        })),
        { role: 'user', content: userMessage }
      ],
      max_tokens: 512,
      temperature: 0.7,
    })
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error?.message || 'Groq API error');
  }

  const data = await res.json();
  const text = data.choices?.[0]?.message?.content;
  if (!text) throw new Error('Koi response nahi aaya');
  return text;
}, [buildSystemPrompt]);

  // ── Send message ─────────────────────────────────────────
  const sendMessage = useCallback(async (text) => {
    const msgText = text || input.trim();
    if (!msgText || loading) return;

    setInput('');
    setShowQuick(false);
    setError(null);

    const userMsg = { role: 'user', content: msgText, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Add to session history
    sessionRef.current.push({ role: 'user', content: msgText });

    try {
      const reply = await callGemini(msgText);
      const botMsg = { role: 'bot', content: reply, timestamp: Date.now() };
      setMessages(prev => [...prev, botMsg]);
      sessionRef.current.push({ role: 'bot', content: reply });

      // Keep history max 10 messages
      if (sessionRef.current.length > 10) {
        sessionRef.current = sessionRef.current.slice(-10);
      }

      // Show new message indicator if chat is closed
      if (!isOpen) setHasNew(true);

    } catch (err) {
      console.error('Gemini error:', err);
      setError('Thodi problem aayi — dobara try karo! 🙏');
      const errMsg = {
        role: 'bot',
        content: 'Maafi chahta hun, abhi thodi technical problem hai. Kuch der baad try karo ya admin se WhatsApp pe contact karo! 🙏',
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, loading, callGemini, isOpen]);

  // ── Handle Enter key ─────────────────────────────────────
  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Clear chat ───────────────────────────────────────────
  const clearChat = () => {
    sessionRef.current = [];
    setShowQuick(true);
    setError(null);
    const greeting = {
      role: 'bot',
      content: `Chat reset ho gaya! 🔄\n\nPhir se — kya poochna hai aapko?`,
      timestamp: Date.now(),
    };
    setMessages([greeting]);
  };

  return (
    <>
      {/* ── FAB Button ─────────────────────────────────── */}
      <button
        className={`chatbot-fab ${isOpen ? 'open' : ''} ${hasNew ? 'has-new' : ''}`}
        onClick={() => setIsOpen(o => !o)}
        aria-label="AI Chatbot"
      >
        {isOpen ? (
          <span className="fab-icon close">✕</span>
        ) : (
          <>
            <span className="fab-icon">✨</span>
            <span className="fab-label">AI Help</span>
            {hasNew && <span className="fab-badge" />}
          </>
        )}
      </button>

      {/* ── Chat Window ────────────────────────────────── */}
      <div className={`chatbot-window ${isOpen ? 'visible' : ''} ${isExpanded ? 'expanded' : ''}`}>

        {/* Header */}
        <div className="chat-header">
          <div className="header-left">
            <div className="header-avatar">
              <span>✨</span>
              <div className="online-dot" />
            </div>
            <div className="header-info">
              <h4>DecorHub AI</h4>
              <span className="header-status">
                {loading ? '⌛ Soch raha hun...' : '🟢 Online — Hinglish mein poochho!'}
              </span>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={clearChat} title="Chat clear karo" className="icon-btn">🔄</button>
            <button onClick={() => setIsExpanded(e => !e)} title="Expand" className="icon-btn">
              {isExpanded ? '⊡' : '⊞'}
            </button>
            <button onClick={() => setIsOpen(false)} title="Band karo" className="icon-btn">✕</button>
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages">
          {messages.map((msg, i) => (
            <MessageBubble
              key={i}
              msg={msg}
              isLatest={i === messages.length - 1}
            />
          ))}
          {loading && (
            <div className="msg-row bot">
              <div className="bot-avatar"><span>✨</span></div>
              <div className="bubble bot-bubble">
                <TypingDots />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick prompts */}
        {showQuick && (
          <div className="quick-prompts">
            <p className="quick-label">💡 Jaldi poochho:</p>
            <div className="quick-grid">
              {QUICK_PROMPTS.map((q, i) => (
                <button
                  key={i}
                  className="quick-btn"
                  onClick={() => sendMessage(q.text)}
                  disabled={loading}
                >
                  {q.icon} {q.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="chat-error">{error}</div>
        )}

        {/* Input */}
        <div className="chat-input-area">
          <div className="input-wrapper">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Kuch bhi poochho... decoration, price, booking 😊"
              rows={1}
              disabled={loading}
              className="chat-textarea"
            />
            <button
              className={`send-btn ${input.trim() ? 'active' : ''}`}
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
            >
              {loading ? '⏳' : '➤'}
            </button>
          </div>
          <p className="powered-by">⚡ Powered by Gemini AI</p>
        </div>
      </div>
    </>
  );
}
